// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { X, Save, Upload, Image, Tag, User, Book, Calendar, Star, FileText, Globe, Hash, Building, Hash as HashIcon } from 'lucide-react';
import { apiFetch } from '../config/api';
import { handleApiError } from '../utils/notificationService';
import notificationService from '../utils/notificationService';
import './EditBookModal.css';

const EditBookModal = ({ book, isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    author: '',
    series: '',
    volume: '',
    year: '',
    isbn: '',
    lang: 'fr',
    tags: [],
    genre: '',
    notes: '',
    rating: 0,
    publisher: '',
    pages: '',
    description: '',
    bisac: '',
    dewey: '',
    difficulty: '',
    doc_type: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [newTag, setNewTag] = useState('');
  // Validation côté client : { field: 'message' }
  const [fieldErrors, setFieldErrors] = useState({});

  /**
   * Valide les métadonnées avant envoi. Retourne { ok, errors }.
   * Règles : titre requis (non-vide après trim), année dans [1000, currentYear+1],
   * pages > 0, volume > 0, ISBN à 10 ou 13 chiffres une fois normalisé (tirets/espaces retirés).
   */
  const validateForm = (data) => {
    const errors = {};
    const currentYear = new Date().getFullYear();

    if (!String(data.title || '').trim()) {
      errors.title = 'Le titre est requis.';
    }

    if (data.year !== '' && data.year != null) {
      const y = parseInt(data.year, 10);
      if (Number.isNaN(y) || y < 1000 || y > currentYear + 1) {
        errors.year = `Année invalide (entre 1000 et ${currentYear + 1}).`;
      }
    }

    if (data.pages !== '' && data.pages != null) {
      const p = parseInt(data.pages, 10);
      if (Number.isNaN(p) || p < 1) {
        errors.pages = 'Le nombre de pages doit être au moins 1.';
      }
    }

    if (data.volume !== '' && data.volume != null) {
      const v = parseInt(data.volume, 10);
      if (Number.isNaN(v) || v < 1) {
        errors.volume = 'Le volume doit être au moins 1.';
      }
    }

    if (data.isbn && String(data.isbn).trim()) {
      const normalized = String(data.isbn).replace(/[-\s]/g, '');
      if (!/^\d{10}$|^\d{13}$/.test(normalized)) {
        errors.isbn = 'ISBN invalide : 10 ou 13 chiffres attendus.';
      }
    }

    return { ok: Object.keys(errors).length === 0, errors };
  };

  // Initialiser les données du formulaire quand le livre change
  useEffect(() => {
    if (book) {
      setFormData({
        title: book.title || '',
        author: book.author || '',
        series: book.series || '',
        volume: book.volume || '',
        year: book.year || '',
        isbn: book.isbn || '',
        lang: book.lang || 'fr',
        tags: book.tags_json ? (() => {
          try {
            return JSON.parse(book.tags_json);
          } catch {
            return [];
          }
        })() : [],
        genre: book.genre || '',
        notes: book.notes || '',
        rating: book.rating || 0,
        publisher: book.publisher || '',
        pages: book.pages || '',
        description: book.description || '',
        bisac: book.bisac || '',
        dewey: book.dewey || '',
        difficulty: book.difficulty || '',
        doc_type: book.doc_type || ''
      });
    }
  }, [book]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Efface l'erreur du champ dès que l'utilisateur le corrige.
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleTagAdd = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const handleTagRemove = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleTagKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleTagAdd();
    }
  };

  const handleCoverChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCoverFile(file);
      
      // Créer un aperçu
      const reader = new FileReader();
      reader.onload = (e) => {
        setCoverPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation client avant tout appel réseau (AUD-008).
    const { ok, errors } = validateForm(formData);
    if (!ok) {
      setFieldErrors(errors);
      notificationService.showToast(
        '⚠️ Corrigez les champs en erreur avant de sauvegarder',
        'warning'
      );
      return;
    }
    setFieldErrors({});

    setLoading(true);

    try {
      // Préparer les données à envoyer
      const updateData = { ...formData };
      // Normalise l'ISBN avant envoi (retire tirets/espaces) pour cohérence côté backend.
      if (updateData.isbn) {
        updateData.isbn = String(updateData.isbn).replace(/[-\s]/g, '');
      }
      // Trim du titre validé
      updateData.title = String(updateData.title).trim();
      
      // Si nous avons un fichier de couverture, utiliser FormData
      if (coverFile) {
        const formData = new FormData();
        
        // Ajouter les métadonnées
        Object.keys(updateData).forEach(key => {
          if (key === 'tags') {
            formData.append(key, JSON.stringify(updateData[key]));
          } else {
            formData.append(key, updateData[key]);
          }
        });
        
        // Ajouter le fichier de couverture
        formData.append('cover', coverFile);
        
        // Appeler l'API avec FormData
        const response = await apiFetch(`books/${book.book_id}`, {
          method: 'PUT',
          body: formData
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const result = await response.json();
        
        notificationService.showToast('✅ Métadonnées mises à jour avec succès', 'success');
        
        // Appeler le callback de sauvegarde
        if (onSave) {
          onSave(result.book);
        }
        
        onClose();
      } else {
        // Pas de fichier, utiliser l'endpoint principal avec FormData
        const formData = new FormData();
        
        // Ajouter les métadonnées
        Object.keys(updateData).forEach(key => {
          if (key === 'tags') {
            formData.append(key, JSON.stringify(updateData[key]));
          } else {
            formData.append(key, updateData[key]);
          }
        });
        
        // Appeler l'API avec FormData
        const response = await apiFetch(`books/${book.book_id}`, {
          method: 'PUT',
          body: formData
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const result = await response.json();

        notificationService.showToast('✅ Métadonnées mises à jour avec succès', 'success');
        
        // Appeler le callback de sauvegarde
        if (onSave) {
          onSave(result.book);
        }
        
        onClose();
      }
      
    } catch (error) {
      handleApiError(error, 'Mise à jour des métadonnées');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !book) return null;

  return (
    <div className="edit-book-modal-overlay">
      <div className="edit-book-modal">
        <div className="edit-book-modal-header">
          <h2>Modifier les métadonnées</h2>
          <button onClick={onClose} className="close-btn">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="edit-book-form">
          <div className="form-sections">
            {/* Section Informations de base */}
            <div className="form-section">
              <h3>
                <Book size={20} />
                Informations de base
              </h3>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="title">Titre *</label>
                  <input
                    type="text"
                    id="title"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    required
                    placeholder="Titre du livre"
                    aria-invalid={Boolean(fieldErrors.title)}
                    aria-describedby={fieldErrors.title ? 'title-error' : undefined}
                  />
                  {fieldErrors.title && (
                    <p id="title-error" className="field-error" role="alert">{fieldErrors.title}</p>
                  )}
                </div>
                
                <div className="form-group">
                  <label htmlFor="author">Auteur</label>
                  <div className="input-with-icon">
                    <User size={16} />
                    <input
                      type="text"
                      id="author"
                      value={formData.author}
                      onChange={(e) => handleInputChange('author', e.target.value)}
                      placeholder="Nom de l'auteur"
                    />
                  </div>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="series">Série</label>
                  <input
                    type="text"
                    id="series"
                    value={formData.series}
                    onChange={(e) => handleInputChange('series', e.target.value)}
                    placeholder="Nom de la série"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="volume">Volume</label>
                  <input
                    type="number"
                    id="volume"
                    value={formData.volume}
                    onChange={(e) => handleInputChange('volume', e.target.value)}
                    placeholder="Numéro de volume"
                    min="1"
                    aria-invalid={Boolean(fieldErrors.volume)}
                    aria-describedby={fieldErrors.volume ? 'volume-error' : undefined}
                  />
                  {fieldErrors.volume && (
                    <p id="volume-error" className="field-error" role="alert">{fieldErrors.volume}</p>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="year">Année</label>
                  <div className="input-with-icon">
                    <Calendar size={16} />
                    <input
                      type="number"
                      id="year"
                      value={formData.year}
                      onChange={(e) => handleInputChange('year', e.target.value)}
                      placeholder="Année de publication"
                      min="1000"
                      max={new Date().getFullYear() + 1}
                      aria-invalid={Boolean(fieldErrors.year)}
                      aria-describedby={fieldErrors.year ? 'year-error' : undefined}
                    />
                  </div>
                  {fieldErrors.year && (
                    <p id="year-error" className="field-error" role="alert">{fieldErrors.year}</p>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="isbn">ISBN</label>
                  <input
                    type="text"
                    id="isbn"
                    value={formData.isbn}
                    onChange={(e) => handleInputChange('isbn', e.target.value)}
                    placeholder="ISBN (10 ou 13 chiffres)"
                    aria-invalid={Boolean(fieldErrors.isbn)}
                    aria-describedby={fieldErrors.isbn ? 'isbn-error' : undefined}
                  />
                  {fieldErrors.isbn && (
                    <p id="isbn-error" className="field-error" role="alert">{fieldErrors.isbn}</p>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="lang">Langue</label>
                  <div className="input-with-icon">
                    <Globe size={16} />
                    <select
                      id="lang"
                      value={formData.lang}
                      onChange={(e) => handleInputChange('lang', e.target.value)}
                    >
                      <option value="fr">Français</option>
                      <option value="en">Anglais</option>
                      <option value="es">Espagnol</option>
                      <option value="de">Allemand</option>
                      <option value="it">Italien</option>
                      <option value="pt">Portugais</option>
                      <option value="ru">Russe</option>
                      <option value="ja">Japonais</option>
                      <option value="zh">Chinois</option>
                      <option value="ar">Arabe</option>
                    </select>
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="rating">Note</label>
                  <div className="rating-input">
                    <Star size={16} />
                    <select
                      id="rating"
                      value={formData.rating}
                      onChange={(e) => handleInputChange('rating', parseInt(e.target.value))}
                    >
                      <option value={0}>Non noté</option>
                      <option value={1}>1 étoile</option>
                      <option value={2}>2 étoiles</option>
                      <option value={3}>3 étoiles</option>
                      <option value={4}>4 étoiles</option>
                      <option value={5}>5 étoiles</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Section Tags */}
            <div className="form-section">
              <h3>
                <Tag size={20} />
                Tags
              </h3>
              
              <div className="tags-container">
                <div className="tags-input">
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={handleTagKeyPress}
                    placeholder="Ajouter un tag..."
                  />
                  <button type="button" onClick={handleTagAdd} className="add-tag-btn">
                    +
                  </button>
                </div>
                
                <div className="tags-list">
                  {formData.tags.map((tag, index) => (
                    <span key={index} className="tag-item">
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleTagRemove(tag)}
                        className="remove-tag-btn"
                      >
                        <X size={16} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Section Métadonnées avancées */}
            <div className="form-section">
              <h3>
                <HashIcon size={20} />
                Métadonnées avancées
              </h3>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="genre">Genre</label>
                  <select
                    id="genre"
                    value={formData.genre}
                    onChange={(e) => handleInputChange('genre', e.target.value)}
                  >
                    <option value="">Sélectionner un genre</option>
                    <option value="fiction">Fiction</option>
                    <option value="non-fiction">Non-fiction</option>
                    <option value="science-fiction">Science-fiction</option>
                    <option value="fantasy">Fantasy</option>
                    <option value="mystery">Mystère</option>
                    <option value="romance">Romance</option>
                    <option value="biography">Biographie</option>
                    <option value="history">Histoire</option>
                    <option value="science">Science</option>
                    <option value="philosophy">Philosophie</option>
                    <option value="religion">Religion</option>
                    <option value="politics">Politique</option>
                    <option value="economics">Économie</option>
                    <option value="technology">Technologie</option>
                    <option value="art">Art</option>
                    <option value="music">Musique</option>
                    <option value="sports">Sport</option>
                    <option value="travel">Voyage</option>
                    <option value="cookbook">Cuisine</option>
                    <option value="self-help">Développement personnel</option>
                    <option value="children">Jeunesse</option>
                    <option value="poetry">Poésie</option>
                    <option value="drama">Théâtre</option>
                    <option value="comics">Bande dessinée</option>
                    <option value="academic">Académique</option>
                    <option value="technical">Technique</option>
                    <option value="manual">Manuel</option>
                    <option value="other">Autre</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="difficulty">Niveau de difficulté</label>
                  <select
                    id="difficulty"
                    value={formData.difficulty}
                    onChange={(e) => handleInputChange('difficulty', e.target.value)}
                  >
                    <option value="">Sélectionner un niveau</option>
                    <option value="beginner">Débutant</option>
                    <option value="intermediate">Intermédiaire</option>
                    <option value="advanced">Avancé</option>
                    <option value="expert">Expert</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="publisher">Éditeur</label>
                  <div className="input-with-icon">
                    <Building size={16} />
                    <input
                      type="text"
                      id="publisher"
                      value={formData.publisher}
                      onChange={(e) => handleInputChange('publisher', e.target.value)}
                      placeholder="Nom de l'éditeur"
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="pages">Nombre de pages</label>
                  <input
                    type="number"
                    id="pages"
                    value={formData.pages}
                    onChange={(e) => handleInputChange('pages', e.target.value)}
                    placeholder="Nombre de pages"
                    min="1"
                    aria-invalid={Boolean(fieldErrors.pages)}
                    aria-describedby={fieldErrors.pages ? 'pages-error' : undefined}
                  />
                  {fieldErrors.pages && (
                    <p id="pages-error" className="field-error" role="alert">{fieldErrors.pages}</p>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="bisac">Classification BISAC</label>
                  <input
                    type="text"
                    id="bisac"
                    value={formData.bisac}
                    onChange={(e) => handleInputChange('bisac', e.target.value)}
                    placeholder="Code BISAC"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="dewey">Classification Dewey</label>
                  <input
                    type="text"
                    id="dewey"
                    value={formData.dewey}
                    onChange={(e) => handleInputChange('dewey', e.target.value)}
                    placeholder="Code Dewey"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="doc_type">Type de document</label>
                <select
                  id="doc_type"
                  value={formData.doc_type}
                  onChange={(e) => handleInputChange('doc_type', e.target.value)}
                >
                  <option value="">Sélectionner un type</option>
                  <option value="book">Livre</option>
                  <option value="academic">Document académique</option>
                  <option value="technical">Document technique</option>
                  <option value="manual">Manuel</option>
                  <option value="guide">Guide</option>
                  <option value="reference">Ouvrage de référence</option>
                  <option value="journal">Journal</option>
                  <option value="magazine">Magazine</option>
                  <option value="newspaper">Journal</option>
                  <option value="report">Rapport</option>
                  <option value="thesis">Thèse</option>
                  <option value="dissertation">Dissertation</option>
                  <option value="other">Autre</option>
                </select>
              </div>
            </div>

            {/* Section Description et notes */}
            <div className="form-section">
              <h3>
                <FileText size={20} />
                Description et notes
              </h3>
              
              <div className="form-group">
                <label htmlFor="description">Description / Synopsis</label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Description ou synopsis du livre..."
                  rows={4}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="notes">Notes personnelles</label>
                <textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  placeholder="Vos notes personnelles sur ce livre..."
                  rows={3}
                />
              </div>
            </div>

            {/* Section Couverture */}
            <div className="form-section">
              <h3>
                <Image size={20} />
                Couverture
              </h3>
              
              <div className="cover-upload">
                <div className="cover-preview">
                  {coverPreview ? (
                    <img src={coverPreview} alt="Aperçu de la couverture" />
                  ) : (
                    <div className="cover-placeholder">
                      <Image size={48} />
                      <p>Aucune couverture sélectionnée</p>
                    </div>
                  )}
                </div>
                
                <div className="cover-upload-controls">
                  <label htmlFor="cover-file" className="upload-btn">
                    <Upload size={16} />
                    Choisir une image
                  </label>
                  <input
                    type="file"
                    id="cover-file"
                    accept="image/*"
                    onChange={handleCoverChange}
                    style={{ display: 'none' }}
                  />
                  
                  {coverFile && (
                    <button
                      type="button"
                      onClick={() => {
                        setCoverFile(null);
                        setCoverPreview(null);
                      }}
                      className="remove-cover-btn"
                    >
                      <X size={16} />
                      Supprimer
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="form-actions">
            <button type="button" onClick={onClose} className="cancel-btn">
              Annuler
            </button>
            <button type="submit" disabled={loading} className="save-btn">
              {loading ? (
                <>
                  <div className="spinner"></div>
                  Sauvegarde...
                </>
              ) : (
                <>
                  <Save size={16} />
                  Sauvegarder
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditBookModal;
